<script setup lang="ts">
import Dialog from "primevue/dialog";
import Sidebar from "primevue/sidebar";
import { type Component, computed, inject, type Ref, ref, toValue } from "vue";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import { ExcursionsKey } from "@/components/RegistrationProcess/composables/useExcursions.ts";
import type { ConfiguratorOptions, ExcursionRules } from "@/components/RegistrationProcess/types/configurator.ts";
import type { EventWithLocations, Excursion, Location } from "@/components/RegistrationProcess/types/events.ts";
import { getEventDisplayLocation } from "@/components/RegistrationProcess/utils/eventDisplayLocation";
import { getEventDuration } from "@/components/RegistrationProcess/utils/eventDuration";
import AddIcon from "@/components/shared/icons/AddIcon.vue";
import Table from "@/components/shared/Table/SimpleTable.vue";
import { useMobile } from "@/composables/useMobile";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import { COMMUNICATION, MOBILITY, SENSORY } from "@/constants.js";
import SubtitleSection from "../../Housing/SubtitleSection.vue";
import MinusSignCircle from "../../widgets/MinusSignCircle.vue";
import ExcursionImages from "../ExcursionImages/index.vue";
import { excursionCardDetailPresets } from "./styles.ts";
import type { Props } from "./types";

defineOptions({
  name: "ExcursionCardDetail",
});

const modelValue = defineModel<Excursion>({ required: true });
const visible = defineModel<boolean>("visible", { default: true });

withDefaults(defineProps<Props>(), {
  title: "ExcursionCardDetail",
});

// The storefront color stays on the add/remove button and the gallery arrows. The chrome (header
// band, close icon) and every piece of copy follow the flag-off convention: gray surfaces with
// navy text for branded tenants, unbranded API tenants unchanged.
const { secondaryColor, secondaryColorClass } = useTenantTheming();

const accentSurfaceClass = computed<string>(() => (secondaryColor.value ? "bg-gray-275" : "bg-teal-400/20"));
const accentTextClass = computed<string>(() =>
  secondaryColor.value ? "text-university-primary" : "text-university-secondary",
);

const presets = computed(() =>
  excursionCardDetailPresets({
    // The header band has its own unbranded color, so it can't share `accentSurfaceClass`.
    headerBackground: secondaryColor.value ? "bg-gray-275" : "bg-teal-light",
    closeIconText: accentTextClass.value,
  }),
);

const { excursions, removeRegistration, addRegistration } = inject(ExcursionsKey)!;
const excursionRules = inject<Readonly<Ref<ExcursionRules>>>("excursionRules")!;
const { configuratorOptions } = inject<{ configuratorOptions: Ref<ConfiguratorOptions> }>("configuratorOptions")!;

const { isMobile, isNarrowViewport } = useMobile();

// The container swap is driven by viewport width alone — deliberately *not* `isMobile`,
// which also matches coarse-pointer tablets and touch laptops at desktop widths. Tracking
// the `md` breakpoint keeps the container in lockstep with the `md:` styling in the body,
// so a landscape iPad gets the desktop dialog and the desktop type scale together.
const isSheet = isNarrowViewport;

const containerComponent = computed<Component>(() => (isSheet.value ? Sidebar : Dialog));

const containerProps = computed<Record<string, unknown>>(() =>
  isSheet.value
    ? {
        position: "bottom",
        modal: true,
        dismissable: true,
        blockScroll: true,
        pt: presets.value.sheet,
      }
    : {
        showHeader: true,
        closable: true,
        modal: true,
        dismissableMask: true,
        closeOnEscape: true,
        draggable: false,
        resizable: false,
        style: { width: "67vw" },
        pt: presets.value.dialog,
      },
);

// Swipe-down-to-dismiss on the sheet's header, so the grab handle is a working
// affordance rather than decoration. The mask tap and close button still apply.
const SWIPE_DISMISS_THRESHOLD_PX = 60;
const touchStartY = ref<number | undefined>(undefined);

const onHeaderTouchStart = (event: TouchEvent): void => {
  touchStartY.value = event.touches[0]?.clientY;
};

const onHeaderTouchEnd = (event: TouchEvent): void => {
  const startY = touchStartY.value;
  touchStartY.value = undefined;
  const endY = event.changedTouches[0]?.clientY;

  if (startY === undefined || endY === undefined) return;
  if (endY - startY >= SWIPE_DISMISS_THRESHOLD_PX) {
    visible.value = false;
  }
};

const selectExcursion = () => {
  const excursionId = modelValue.value.id;
  if (excursions.eventInstanceIds.includes(excursionId)) {
    removeRegistration(excursionId);
  } else {
    addRegistration(excursionId);
  }
  visible.value = false;
};

const optionsPricing = computed(() => {
  const excursions = toValue(configuratorOptions).event_occurrences;
  return excursions.find((e) => e.productId === modelValue.value?.id);
});

const price = computed<string>(() => {
  return optionsPricing.value?.excursionPriceLabel(false) ?? "";
});

const event = computed<EventWithLocations>(() => modelValue.value?.event ?? {});

const locations = computed<Location[]>(() => event.value?.event_locations ?? []);
const location = computed<Location>(() => locations.value[0] ?? {});
const address = computed<string>(() => getEventDisplayLocation(event.value));

const eventTitle = computed<string>(() => {
  return event.value.title ?? "";
});

const duration = computed<string>(() => getEventDuration(event.value));

const description = computed<Record<string, any> | undefined>(() => {
  return event.value.description as Record<string, any>;
});

const subcategory = computed<string>(() => event.value.event_subcategory?.name ?? "");

const isExcursionIncluded = computed<boolean>(() => {
  return (
    excursionRules?.value?.defaultIds?.includes(modelValue.value?.id) ||
    excursionRules?.value?.andIds?.includes(modelValue.value?.id)
  );
});

const isExcursionSelected = computed<boolean>(() => {
  return excursions.eventInstanceIds.includes(modelValue.value.id);
});

const selectionText = computed<string>(() => {
  return isExcursionSelected.value ? "Selected" : isMobile.value ? "Add" : "Add excursion";
});

const transformAccessibilityItem = (values: string, validValues: { id: number; name: string }[]): string[] => {
  const ids = JSON.parse(values) ?? [];

  return validValues.filter((item) => ids.includes(item.id)).map((item) => item.name);
};

const accessibility = computed<string[]>(() => {
  const communication = transformAccessibilityItem(location.value?.communication ?? "[]", COMMUNICATION);
  const mobility = transformAccessibilityItem(location.value?.mobility ?? "[]", MOBILITY);
  const sensory = transformAccessibilityItem(location.value?.sensory ?? "[]", SENSORY);

  return [...communication, ...mobility, ...sensory];
});

const includes = computed(() => event.value?.includes ?? []);

interface TableRow {
  title: string;
  type: string;
  content: string;
  filled: boolean;
}

const tableData = computed(() => {
  const tableRows: TableRow[] = [];
  let filled = false;

  if (includes.value.length) {
    filled = !filled;
  }

  if (event.value.transportation) {
    filled = !filled;

    tableRows.push({
      title: "Transportation",
      type: "simple-text",
      content: event.value.transportation,
      filled: filled,
    });
  }

  if (event.value.activity_level) {
    filled = !filled;

    tableRows.push({
      title: "Activity Level",
      type: "simple-text",
      content: event.value.activity_level,
      filled: filled,
    });
  }

  if (event.value.packing_list?.length) {
    filled = !filled;

    tableRows.push({
      title: "What to pack",
      type: "simple-text",
      content: event.value.packing_list.join(",  "),
      filled: filled,
    });
  }

  if (accessibility.value.length) {
    filled = !filled;

    tableRows.push({
      title: "Accessibility",
      type: "simple-text",
      content: accessibility.value.join(",  "),
      filled: filled,
    });
  }

  if (event.value.host) {
    filled = !filled;

    tableRows.push({
      title: "Organized By",
      type: "simple-text",
      content: event.value.host,
      filled: filled,
    });
  }

  return tableRows;
});
</script>

<template>
  <component :is="containerComponent" v-model:visible="visible" v-bind="containerProps">
    <template #header>
      <!-- Sheet: grab handle over a compact one-line title. The body's <h1> carries the
           full event name, so repeating it in full here only costs vertical space. -->
      <div
        v-if="isSheet"
        class="min-w-0 flex-1"
        @touchstart.passive="onHeaderTouchStart"
        @touchend.passive="onHeaderTouchEnd"
      >
        <span
          class="absolute left-1/2 top-2.5 h-1 w-10 -translate-x-1/2 rounded-full bg-gray-25"
          data-testid="excursion-card-detail-sheet-handle"
        />
        <h3 class="text-base font-bold line-clamp-1" data-testid="excursion-card-detail-header">{{ eventTitle }}</h3>
      </div>
      <h3 v-else class="text-2xl font-bold" data-testid="excursion-card-detail-header">{{ eventTitle }} - Details</h3>
    </template>
    <div class="bg-white text-university-primary" data-testid="excursion-card-detail">
      <div class="m-4 md:m-14">
        <div class="md:flex items-stretch gap-10">
          <!-- `min-w-0` on both columns: flex items default to `min-width: auto`, so without it
               a wide child or an unbroken word overrides the declared width and spills over the
               neighbouring column. -->
          <div class="md:w-2/4 lg:w-7/12 h-full min-h-0 min-w-0 flex flex-col">
            <ExcursionImages :model-value="event.image ?? ''" :image-alt="eventTitle" :resolution="'high'" />
          </div>
          <div class="md:flex-1 min-w-0 flex flex-col mt-4 md:mt-0">
            <div class="flex-grow">
              <div
                class="flex flex-col items-start gap-2 md:flex-row md:flex-wrap md:gap-0 md:justify-between md:items-end pb-3 mb-3 border-b border-gray-25"
              >
                <p class="uppercase text-sm font-bold pt-0 md:pt-4" data-testid="excursion-card-detail-subcategory">
                  {{ subcategory }}
                </p>
                <div class="flex gap-2 order-first md:order-last">
                  <p class="py-1 px-2 uppercase font-bold" :class="`${accentSurfaceClass} ${accentTextClass}`" data-testid="excursion-card-detail-price">
                    {{ price }}
                  </p>
                </div>
              </div>
              <h1 class="text-2xl md:text-4xl font-bold leading-none break-words text-university-primary" data-testid="excursion-card-detail-title">
                {{ eventTitle }}
              </h1>

              <div class="mt-2 text-sm" data-testid="excursion-card-detail-location">{{ address }}</div>

              <div v-if="duration" class="mt-2 text-sm whitespace-pre-wrap" data-testid="excursion-card-detail-duration">
                {{ duration }}
              </div>
            </div>

            <div v-if="!isExcursionIncluded" class="align-baseline mt-6 md:mt-10 flex md:justify-end">
              <button
                class="flex relative items-center justify-center gap-2 p-4 font-bold uppercase border-2 w-full md:w-auto"
                :class="secondaryColorClass"
                data-testid="excursion-card-detail-toggle-button"
                @click="selectExcursion()"
              >
              <span>
                {{ selectionText }}
              </span>
              <div v-if="isExcursionSelected" class="absolute -top-3 -right-3">
                <MinusSignCircle />
              </div>
              <div v-else>
                <AddIcon :width="20" :height="20" :hasBorderCircle="false" />
              </div>
              </button>
            </div>
          </div>
        </div>

        <div v-if="description" class="mt-6 md:mt-10 gap-10">
          <JSONtoHTML class="w-full text-base md:text-lg font-medium text-university-primary" :tiptap-json="description" data-testid="excursion-card-detail-description" />
          <div class="md:w-2/4 lg:w-7/12">
            <div class="mt-10 md:mt-20" data-testid="excursion-card-detail-event-details">
              <SubtitleSection title="Event details" />
              <Table :data="tableData" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<style scoped></style>
